import { lazy, Suspense, useEffect, useRef } from "react";
import {
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import Modal from "./components/Modal";
import PrivateRoute from "./components/PrivateRoute";
import SalesSuspendedRoute from "./components/SalesSuspendedRoute";
import PreviewAdminRoute from "./anamnese-v2/components/PreviewAdminRoute";
import AnamneseVersionGate from "./components/AnamneseVersionGate";
import { ProcessingProvider, useProcessing } from "./context/ProcessingContext";
import { ToastProvider } from "./context/ToastContext";
import { UserProvider } from "./context/UserContext";
import ScrollToTop from "./ScrollToTop";
// Clarity é carregado via dynamic import para não inflar o main bundle
import logoPurissima from "./assets/images/svg/unicornio-brown.svg";

import ChatButton from "./components/ChatButton";
import ChatLoader from "./components/ChatLoader";
import Footer from "./components/Footer";
import { ensureAnamneseConfig } from "./utils/anamneseVersion";
import { ensureCampaignCoupon } from "./utils/campaignCoupon";

// Páginas carregadas sob demanda (code splitting)
const Convite = lazy(() => import("./pages/lps/lp-convite/Convite"));
const Home = lazy(() => import("./pages/Home"));
const Impersonate = lazy(() => import("./pages/Impersonate"));
const AtualizarSabor = lazy(() => import("./pages/lps/AtualizarSabor"));
const Emagrecimento = lazy(
  () => import("./pages/lps/lp-emagrecimento/Emagrecimento"),
);
const SaudeMuscular = lazy(
  () => import("./pages/lps/lp-muscular/SaudeMuscular"),
);
const Menopausa = lazy(() => import("./pages/lps/lp-menopausa/Menopausa"));
const Calma = lazy(() => import("./pages/lps/lp-calma/Calma"));
const Prosperidade = lazy(
  () => import("./pages/lps/lp-prosperidade/Prosperidade"),
);
const Lancamento = lazy(() => import("./pages/lps/Lancamento"));
const Receita = lazy(() => import("./pages/Receita"));
const Basico = lazy(() => import("./pages/Basico"));
const Anamnese = lazy(() => import("./pages/Anamnese"));
const ComoTomar = lazy(() => import("./pages/ComoTomar"));
const Consulta = lazy(() => import("./pages/Consulta"));
const Farmacoteca = lazy(() => import("./pages/Farmacoteca"));
const IngredientesRoute = lazy(() => import("./pages/IngredientesRoute"));
const HealthChartResult = lazy(() => import("./pages/HealthChartResult"));
const Nutraceuticos = lazy(() => import("./pages/Nutraceuticos"));
const Omega = lazy(() => import("./pages/Omega"));
const Questionario = lazy(() => import("./pages/Questionario"));
const Sobre = lazy(() => import("./pages/Sobre"));
const FlavorsCheckout = lazy(() => import("./pages/FlavorsCheckout"));
const Privacy = lazy(() => import("./pages/Privacy"));
const SubscriptionFlavorChange = lazy(
  () => import("./pages/SubscriptionFlavorChange"),
);
const Cadastro = lazy(() => import("./pages/Cadastro"));
const CadastroFull = lazy(() => import("./pages/CadastroFull"));
const CartPage = lazy(() => import("./pages/CartPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Concluido = lazy(() => import("./pages/Concluido"));
const ConsultaDigital = lazy(() => import("./pages/ConsultaDigital"));
const Fechamento = lazy(() => import("./pages/Fechamento"));
const Inauguration = lazy(() => import("./pages/Inauguration"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const PasswordChange = lazy(() => import("./pages/PasswordChange"));
const PurissimaSolutions = lazy(() => import("./pages/PurissimaSolutions"));
const Login = lazy(() => import("./pages/Login"));
const Logout = lazy(() => import("./pages/Logout"));
const Recovery = lazy(() => import("./pages/Recovery"));
const SolucoesKids = lazy(() => import("./pages/SolucoesKids"));
const SolucoesBaby = lazy(() => import("./pages/SolucoesBaby"));
const SolucoesNutraceuticas = lazy(
  () => import("./pages/SolucoesNutraceuticas"),
);
const NotFound = lazy(() => import("./pages/NotFound"));
// const Prescritor = lazy(() => import("./pages/Prescritor"));
const ReturnsAndRefunds = lazy(() => import("./pages/ReturnsAndRefunds"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));

// Descubra é carregada via lazy para isolar o bundle da LP de marketing
const Descubra = lazy(() => import("./pages/lps/Descubra"));
const DescubraV2 = lazy(() => import("./pages/lps/DescubraV2"));
const DescubraV3 = lazy(() => import("./pages/lps/DescubraV3"));
const DescubraV4 = lazy(() => import("./pages/lps/DescubraV4"));
const PgEmagrecimento1 = lazy(() => import("./pages/lps/PgEmagrecimento1"));
const PgEmagrecimento2 = lazy(() => import("./pages/lps/PgEmagrecimento2"));
const LpVideo = lazy(() => import("./pages/lps/LpVideo"));
const FrequenciaCompra = lazy(() => import("./pages/FrequenciaCompra"));
const NutraceuticosOpcionais = lazy(
  () => import("./pages/NutraceuticosOpcionais"),
);
const Triagem = lazy(() => import("./pages/anamnese/Triagem"));
const TriagemEtapa = lazy(() => import("./pages/anamnese/TriagemEtapa"));

// Anamnese v2: rotas paralelas ao fluxo legado até o cutover.
const QuestionnaireV2Page = lazy(() => import("./anamnese-v2/pages/QuestionnairePage"));
const PreviewMapaV2 = lazy(() => import("./anamnese-v2/pages/PreviewMapa"));
const PreviewSolucoesV2 = lazy(() => import("./anamnese-v2/pages/PreviewSolucoes"));

const BibliotecaIndex = lazy(() => import("./pages/biblioteca/BibliotecaIndex"));
const SuplementacaoPersonalizada = lazy(
  () => import("./pages/biblioteca/SuplementacaoPersonalizada"),
);
const SuplementoManipuladoOuIndustrializado = lazy(
  () => import("./pages/biblioteca/SuplementoManipuladoOuIndustrializado"),
);
const MultivitaminicoPersonalizado = lazy(
  () => import("./pages/biblioteca/MultivitaminicoPersonalizado"),
);
const QuantoCustaSuplementacaoPersonalizada = lazy(
  () => import("./pages/biblioteca/QuantoCustaSuplementacaoPersonalizada"),
);
const QuaisSuplementosDevoTomar = lazy(
  () => import("./pages/biblioteca/QuaisSuplementosDevoTomar"),
);
const ProsperidadeNutricional = lazy(
  () => import("./pages/biblioteca/ProsperidadeNutricional"),
);

// Cupom automático da campanha vigente (ver utils/campaignCoupon.js).
// Roda já no carregamento, antes do primeiro render das páginas que leem o percentual.
ensureCampaignCoupon();

function AppContent() {
  const { isProcessing } = useProcessing(); // Acessando o estado global
  const location = useLocation();

  const paginasLP = [
    "/atualizar-sabor",
    "/convite",
    "/emagrecimento",
    "/saude-muscular",
    "/menopausa",
    "/ansiedadeesono",
    "/prosperidade",
    "/captura",
    "/lnc/",
    "/descubra",
    "/v2descubra",
    "/v3descubra",
    "/v4descubra",
    "/pg1.emagrecimento",
    "/pg2.emagrecimento",
    "/vsl",
  ];
  const isLP = paginasLP.some((path) => location.pathname.startsWith(path));
  const needsSupportChat = !isLP || location.pathname.startsWith("/lnc/");

  const hideHeader = [
    "/atualizar-sabor",
    "/coming-soon",
    "/convite",
    "/emagrecimento",
    "/saude-muscular",
    "/menopausa",
    "/ansiedadeesono",
    "/prosperidade",
    "/captura",
    "/lnc/",
    "/descubra",
    "/v2descubra",
    "/v3descubra",
    "/v4descubra",
    "/pg1.emagrecimento",
    "/pg2.emagrecimento",
    "/vsl",
  ];
  const isHeaderHidden = hideHeader.some((path) =>
    location.pathname.startsWith(path),
  );

  const hideFooter = [
    "/atualizar-sabor",
    "/questionario",
    "/anamnese-v2/submissoes",
    "/convite",
    "/emagrecimento",
    "/captura",
    "/lnc/",
    "/lnc.emagrecimento",
    "/saude-muscular",
    "/menopausa",
    "/ansiedadeesono",
    "/prosperidade",
    "/v4descubra",
  ];
  const isFooterHidden =
    hideFooter.some((path) => location.pathname.startsWith(path)) ||
    location.pathname === "/anamnese-v2";

  // rotas que alargam o miolo do conteúdo em telas grandes (ver assets/styles/wide-screen.css)
  const scaleWidePaths = [
    "/minha-conta",
    "/questionario",
    "/anamnese-v2",
    "/mapa-da-saude",
    "/comece-por-aqui",
    "/solucoes-",
    "/conheca-formula",
    "/frequencia",
    "/nutraceuticos-opcionais",
    "/sabores",
    "/assinatura",
    "/cart",
    "/checkout",
  ];
  const isScaleWide = scaleWidePaths.some((path) =>
    location.pathname.startsWith(path),
  );
  useEffect(() => {
    document.body.classList.toggle("scale-wide", isScaleWide);
    return () => document.body.classList.remove("scale-wide");
  }, [isScaleWide]);

  const isStaging = process.env.REACT_APP_ENV !== "production";
  const bannerRef = useRef(null);

  useEffect(() => {
    import("@microsoft/clarity").then(({ default: Clarity }) => {
      Clarity.init("to4b7w7ewo");
    });
  }, []);

  useEffect(() => {
    ensureAnamneseConfig();
  }, []);

  // Captura cupom vindo por link (ex: .../descubra?cupom=CODE&utm_source=...),
  // pra aplicar automaticamente assim que o carrinho tiver itens (ver CartPage.jsx).
  // Global aqui porque o link pode cair em qualquer página, não só no carrinho.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cupom = params.get("cupom") || params.get("coupon");
    if (cupom && cupom.trim()) {
      localStorage.setItem("pendingCouponCode", cupom.trim());
    }
  }, [location.search]);

  // Campanha vigente (ver ensureCampaignCoupon): reaplica a cada mudança de
  // página, caso o cupom tenha sido limpo (ex.: pela página de lançamento).
  useEffect(() => {
    ensureCampaignCoupon();
  }, [location.pathname]);

  useEffect(() => {
    if (!isStaging) {
      document.documentElement.style.removeProperty("--staging-banner-height");
      return;
    }

    const updateBannerHeight = () => {
      if (bannerRef.current) {
        const height = bannerRef.current.offsetHeight;
        document.documentElement.style.setProperty(
          "--staging-banner-height",
          `${height}px`,
        );
      }
    };

    updateBannerHeight();

    let resizeObserver;
    if (bannerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateBannerHeight);
      resizeObserver.observe(bannerRef.current);
    } else {
      window.addEventListener("resize", updateBannerHeight);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", updateBannerHeight);
      }
    };
  }, [isStaging]);

  // useEffect(() => {
  //     const mockData = teste; // over_35 | pregnant | performance | weight
  //     localStorage.setItem('userAnswers', JSON.stringify(mockData));
  // }, []);

  // localStorage.setItem('userAnswers', JSON.stringify({}));
  // console.log("BUILD VERSÃO 2025-04-09");

  return (
    <div className="App">
      {isStaging && (
        <div
          ref={bannerRef}
          style={{
            backgroundColor: "#cc0000",
            color: "#fff",
            textAlign: "center",
            padding: "14px 16px",
            fontWeight: "700",
            fontSize: "16px",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            position: "sticky",
            top: 0,
            zIndex: 9999,
          }}
        >
          ⚠️ AMBIENTE DE HOMOLOGAÇÃO — As informações aqui não são reais ⚠️
        </div>
      )}
      {isProcessing && (
        <div className="processing-message">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 mb-4 text-center">
                <img src={logoPurissima} alt="logoPurissima" />
              </div>
              <div className="col-auto text-center">
                <h4 className="text-white text-uppercase">
                  <strong>Processando</strong>
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastProvider>
        <UserProvider>
          {needsSupportChat && <ChatLoader />}
          {!isLP && <ChatButton />}

          {!isHeaderHidden && <Header />}

          <main>
            <ScrollToTop />
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
              <Routes>
                <Route path="/impersonate" element={<Impersonate />} />
                <Route path="/captura" element={<Emagrecimento />} />
                <Route
                  path="/captura/:captureSlug"
                  element={<Emagrecimento />}
                />

                <Route path="/emagrecimento" element={<Emagrecimento />} />
                <Route
                  path="/emagrecimento/:captureSlug"
                  element={<Emagrecimento />}
                />

                <Route path="/saude-muscular" element={<SaudeMuscular />} />
                <Route
                  path="/saude-muscular/:captureSlug"
                  element={<SaudeMuscular />}
                />

                <Route path="/menopausa" element={<Menopausa />} />
                <Route path="/menopausa/:captureSlug" element={<Menopausa />} />

                <Route path="/ansiedadeesono" element={<Calma />} />
                <Route
                  path="/ansiedadeesono/:captureSlug"
                  element={<Calma />}
                />

                <Route path="/prosperidade" element={<Prosperidade />} />
                <Route
                  path="/prosperidade/:captureSlug"
                  element={<Prosperidade />}
                />

                <Route path="/lnc/:lncSlug" element={<Lancamento />} />
                <Route
                  path="/lnc/:lncSlug/:originSlug"
                  element={<Lancamento />}
                />

                <Route
                  path="/descubra"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <Descubra />
                    </Suspense>
                  }
                />
                <Route
                  path="/v2descubra"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <DescubraV2 />
                    </Suspense>
                  }
                />
                <Route
                  path="/v3descubra"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <DescubraV3 />
                    </Suspense>
                  }
                />
                <Route
                  path="/v4descubra"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <DescubraV4 />
                    </Suspense>
                  }
                />
                <Route
                  path="/pg1.emagrecimento"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <PgEmagrecimento1 />
                    </Suspense>
                  }
                />
                <Route
                  path="/pg2.emagrecimento"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <PgEmagrecimento2 />
                    </Suspense>
                  }
                />
                <Route
                  path="/vsl/:tema"
                  element={
                    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                      <LpVideo />
                    </Suspense>
                  }
                />

                <Route path="/" element={<Home />} />
                <Route path="/wa/comercial" element={<Home />} />
                <Route path="/basico" element={<Basico />} />
                <Route path="/comece-por-aqui" element={<Anamnese />} />

                <Route path="/consulta-digital" element={<ConsultaDigital />} />

                <Route
                  path="/questionario"
                  element={
                    <PrivateRoute>
                      <Questionario />
                    </PrivateRoute>
                  }
                />
                
                <Route
                  path="/questionario/adulto/:sectionId"
                  element={
                    <AnamneseVersionGate mode="legacy-adult">
                      <SalesSuspendedRoute>
                        <PrivateRoute>
                          <Questionario grupoSlug="adulto" />
                        </PrivateRoute>
                      </SalesSuspendedRoute>
                    </AnamneseVersionGate>
                  }
                />
                <Route
                  path="/questionario/adulto"
                  element={
                    <AnamneseVersionGate mode="legacy-adult">
                      <SalesSuspendedRoute>
                        <PrivateRoute>
                          <Questionario grupoSlug="adulto" />
                        </PrivateRoute>
                      </SalesSuspendedRoute>
                    </AnamneseVersionGate>
                  }
                />

                <Route
                  path="/questionario/my-kids/:sectionId"
                  element={
                    <AnamneseVersionGate mode="legacy-kids">
                      <SalesSuspendedRoute>
                        <PrivateRoute>
                          <Questionario grupoSlug="my-kids" />
                        </PrivateRoute>
                      </SalesSuspendedRoute>
                    </AnamneseVersionGate>
                  }
                />
                <Route
                  path="/questionario/my-kids"
                  element={
                    <AnamneseVersionGate mode="legacy-kids">
                      <SalesSuspendedRoute>
                        <PrivateRoute>
                          <Questionario grupoSlug="my-kids" />
                        </PrivateRoute>
                      </SalesSuspendedRoute>
                    </AnamneseVersionGate>
                  }
                />

                {/* Anamnese v2 — convive com o fluxo legado acima até o cutover. */}
                <Route
                  path="/anamnese-v2"
                  element={
                    <AnamneseVersionGate mode="v2-collection">
                      <PrivateRoute>
                        <PreviewAdminRoute>
                          <QuestionnaireV2Page />
                        </PreviewAdminRoute>
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />
                <Route
                  path="/anamnese-v2/submissoes/:submissionUuid"
                  element={
                    <AnamneseVersionGate mode="v2-collection">
                      <PrivateRoute>
                        <PreviewAdminRoute>
                          <QuestionnaireV2Page />
                        </PreviewAdminRoute>
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />
                <Route
                  path="/anamnese-v2/mapa"
                  element={
                    <AnamneseVersionGate mode="v2-collection">
                      <PrivateRoute>
                        <PreviewAdminRoute>
                          <PreviewMapaV2 />
                        </PreviewAdminRoute>
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />
                <Route
                  path="/anamnese-v2/solucoes"
                  element={
                    <PrivateRoute>
                      <PreviewAdminRoute>
                        <PreviewSolucoesV2 />
                      </PreviewAdminRoute>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/mapa-da-saude"
                  element={
                    <AnamneseVersionGate mode="legacy-map">
                      <HealthChartResult />
                    </AnamneseVersionGate>
                  }
                />

                <Route path="/ingredientes" element={<Farmacoteca />} />

                <Route
                  path="/ingredientes/*"
                  element={<IngredientesRoute />}
                />

                <Route path="/biblioteca" element={<BibliotecaIndex />} />
                <Route
                  path="/biblioteca/suplementacao-personalizada"
                  element={<SuplementacaoPersonalizada />}
                />
                <Route
                  path="/biblioteca/suplemento-manipulado-ou-industrializado"
                  element={<SuplementoManipuladoOuIndustrializado />}
                />
                <Route
                  path="/biblioteca/multivitaminico-personalizado"
                  element={<MultivitaminicoPersonalizado />}
                />
                <Route
                  path="/biblioteca/quanto-custa-suplementacao-personalizada"
                  element={<QuantoCustaSuplementacaoPersonalizada />}
                />
                <Route
                  path="/biblioteca/quais-suplementos-devo-tomar"
                  element={<QuaisSuplementosDevoTomar />}
                />
                <Route
                  path="/biblioteca/prosperidade-nutricional"
                  element={<ProsperidadeNutricional />}
                />

                <Route path="/sobre" element={<Sobre />} />
                <Route path="/politica-de-privacidade" element={<Privacy />} />

                <Route
                  path="/termos-e-condicoes"
                  element={<TermsConditions />}
                />

                <Route
                  path="/politica-de-cancelamento"
                  element={<ReturnsAndRefunds />}
                />

                <Route path="/como-tomar" element={<ComoTomar />} />

                <Route
                  path="/solucoes-purissima"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <PurissimaSolutions />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/conheca-formula"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <SolucoesNutraceuticas />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/frequencia"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <FrequenciaCompra />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/nutraceuticos-opcionais"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <NutraceuticosOpcionais />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/questionario/adulto-triagem"
                  element={
                    <AnamneseVersionGate mode="legacy-adult">
                      <PrivateRoute>
                        <Triagem triagemVersion={1} />
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />

                <Route
                  path="/questionario/adulto-triagem3"
                  element={
                    <AnamneseVersionGate mode="legacy-adult">
                      <PrivateRoute>
                        <Triagem triagemVersion={3} />
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />

                <Route
                  path="/questionario/adulto-nutri"
                  element={
                    <AnamneseVersionGate mode="legacy-adult">
                      <PrivateRoute>
                        <TriagemEtapa mode="nutri" />
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />

                <Route
                  path="/questionario/adulto-mapa"
                  element={
                    <AnamneseVersionGate mode="legacy-adult">
                      <PrivateRoute>
                        <TriagemEtapa mode="mapa" />
                      </PrivateRoute>
                    </AnamneseVersionGate>
                  }
                />

                <Route
                  path="/solucoes-kids"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <SolucoesKids />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/solucoes-baby"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <SolucoesBaby />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/sabores"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <FlavorsCheckout />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/atualizar-sabor"
                  element={
                    <SalesSuspendedRoute>
                      <AtualizarSabor />
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/assinatura/:subscriptionId/sabores"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <SubscriptionFlavorChange />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route
                  path="/cart"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <CartPage />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <Checkout />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />
                <Route
                  path="/checkout/fechamento"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <Fechamento />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />
                <Route
                  path="/checkout/fechamento/:id"
                  element={
                    <SalesSuspendedRoute>
                      <PrivateRoute>
                        <Fechamento />
                      </PrivateRoute>
                    </SalesSuspendedRoute>
                  }
                />

                <Route path="/login" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/recuperar" element={<Recovery />} />
                <Route path="/alterar-senha" element={<PasswordChange />} />
                <Route path="/cadastro" element={<Cadastro />} />
                <Route
                  path="/cadastro-full"
                  element={
                    <PrivateRoute>
                      <CadastroFull />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/minha-conta/*"
                  element={
                    <PrivateRoute>
                      <MyAccount />
                    </PrivateRoute>
                  }
                />

                <Route path="/convite" element={<Convite />} />
                {/*         <Route path="/convite-org" element={<Convite />} />
                        <Route path="/convite-org-api" element={<Convite tag="Purissima - LI 0925 - API OFICIAL" />} />
                        <Route path="/convite-org-email" element={<Convite tag="Purissima - LI 0925 - API OFICIAL" />} /> */}

                {/* <Route path="/prescritores" element={<Prescritor />} /> */}

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>

            {!isFooterHidden && <Footer />}
          </main>

          <Modal />
          {/* <GptMakerWidget /> */}
        </UserProvider>
      </ToastProvider>
    </div>
  );
}

function App() {
  return (
    <ProcessingProvider>
      <AppContent />
    </ProcessingProvider>
  );
}

export default App;
